import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Timer, Loader2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { typeMeta, prioMeta } from "@/lib/tickets";
import api, { apiError } from "@/lib/api";

const num = "w-16 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm text-gray-900 text-center font-mono focus:outline-none focus:border-[#c00000]/50";

export default function SlaDialog({ open, onClose, de = true }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings/sla").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e)));
  }, [open]);

  const setCell = (tt, pr, field, v) => setCfg((c) => {
    const n = { ...c, matrix: { ...c.matrix, [tt]: { ...c.matrix[tt], [pr]: { ...c.matrix[tt][pr], [field]: Math.max(0, parseInt(v, 10) || 0) } } } };
    return n;
  });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/sla", { business_hours: cfg.business_hours, biz_start: cfg.biz_start, biz_end: cfg.biz_end, matrix: cfg.matrix });
      setCfg({ ...r.data, ticket_types: cfg.ticket_types, priorities: cfg.priorities });
      toast.success(de ? "SLA gespeichert." : "SLA saved.");
      onClose && onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent data-testid="sla-dialog" className="max-w-3xl bg-gray-50 border-gray-200 text-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900"><Timer className="w-5 h-5 text-[#c00000]" /> {de ? "SLA-Bearbeitungszeiten" : "SLA response times"}</DialogTitle>
          <DialogDescription className="text-gray-600">
            {de ? "Zwei Fristen je Ticket-Typ und Priorität: Kunden-SLA (Gesamtlösung) und interne Supporter-SLA. Angabe in Stunden ab Ticketeingang." : "Two deadlines per type & priority: customer SLA and internal supporter SLA (in hours)."}
          </DialogDescription>
        </DialogHeader>
        {!cfg ? (
          <div className="flex items-center gap-2 text-gray-600 py-8"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
        ) : (
          <div className="max-h-[62vh] overflow-y-auto pr-1">
            <label className="flex items-center gap-3 mb-4 bg-white/[0.03] border border-gray-200 rounded-lg px-3 py-2.5">
              <input data-testid="sla-business-toggle" type="checkbox" checked={cfg.business_hours} onChange={(e) => setCfg({ ...cfg, business_hours: e.target.checked })} className="accent-[#c00000] w-4 h-4" />
              <span className="text-sm text-gray-800">{de ? "Nur Geschäftszeiten rechnen" : "Count business hours only"}</span>
              {cfg.business_hours && (
                <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-600 font-mono">
                  Mo–Fr
                  <input data-testid="sla-biz-start" type="number" min={0} max={23} value={cfg.biz_start} onChange={(e) => setCfg({ ...cfg, biz_start: parseInt(e.target.value, 10) || 0 })} className={num} />–
                  <input data-testid="sla-biz-end" type="number" min={1} max={24} value={cfg.biz_end} onChange={(e) => setCfg({ ...cfg, biz_end: parseInt(e.target.value, 10) || 0 })} className={num} /> Uhr
                </span>
              )}
            </label>
            <div className="space-y-4">
              {cfg.ticket_types.map((tt) => (
                <div key={tt} data-testid={`sla-type-${tt}`} className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className={`px-3 py-2 text-sm font-semibold border-b border-gray-200 ${typeMeta(tt).cls}`}>{de ? typeMeta(tt).de : typeMeta(tt).en}</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] font-mono uppercase tracking-wider text-gray-600">
                        <th className="text-left px-3 py-1.5 font-medium">{de ? "Priorität" : "Priority"}</th>
                        <th className="text-left px-3 py-1.5 font-medium text-[#c00000]">{de ? "Kunden-SLA (Std)" : "Customer SLA (h)"}</th>
                        <th className="text-left px-3 py-1.5 font-medium text-amber-700">{de ? "Interne SLA (Std)" : "Internal SLA (h)"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cfg.priorities.map((pr) => (
                        <tr key={pr} className="border-t border-gray-100">
                          <td className="px-3 py-2"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${prioMeta(pr).cls}`}>{de ? prioMeta(pr).de : prioMeta(pr).en}</span></td>
                          <td className="px-3 py-2"><input data-testid={`sla-${tt}-${pr}-customer`} type="number" min={0} value={cfg.matrix[tt][pr].customer_hours} onChange={(e) => setCell(tt, pr, "customer_hours", e.target.value)} className={num} /></td>
                          <td className="px-3 py-2"><input data-testid={`sla-${tt}-${pr}-internal`} type="number" min={0} value={cfg.matrix[tt][pr].internal_hours} onChange={(e) => setCell(tt, pr, "internal_hours", e.target.value)} className={num} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
          <button onClick={onClose} className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 transition-colors">{de ? "Abbrechen" : "Cancel"}</button>
          <button data-testid="sla-save" onClick={save} disabled={busy || !cfg} className="flex items-center gap-2 bg-[#c00000]/90 hover:bg-[#c00000] text-black font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {de ? "Speichern" : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
